import { API_BASE_URL } from '../config/api'
import { getAuthHeaders, getAuthHeadersAsync } from '../utils/authUtils'
import { isHiddenDirectoryOrg } from '../utils/hiddenDirectoryOrgs'

export interface Donation {
  id: string
  title: string
  description: string
  estimatedWeight: string
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  status: 'NEW' | 'ACCEPTED' | 'PICKED_UP' | 'DELIVERED' | 'CANCELLED' | 'EXPIRED'
  specialInstructions?: string
  contactName: string
  contactPhone: string
  pickupStreet: string
  pickupCity: string
  pickupState: string
  pickupZipCode: string
  pickupCountry: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledDate?: string
  pickupTime?: string
  deliveryTime?: string
  completedDate?: string
  imageUrl?: string | null
  supplierId: string
  volunteerId?: string
  foodBankId?: string
  notes?: string
  createdAt: string
  updatedAt: string
  supplier: {
    id: string
    firstName: string
    lastName: string
    userType: string
  }
  volunteer?: {
    id: string
    firstName: string
    lastName: string
  }
  foodBank?: {
    id: string
    firstName: string
    lastName: string
  }
  items: DonationItem[]
  
  // Legacy properties for backward compatibility
  pickupAddress?: string
  deliveryAddress?: string
  donor?: {
    id: string
    firstName: string
    lastName: string
    userType: string
  }
  donationLat?: number
  donationLng?: number
}

export interface DonationItem {
  id: string
  name: string
  quantity: number
  unit: string
  category: string
  expirationDate: string
  isPerishable: boolean
  description?: string
}

export interface CreateDonationData {
  title: string
  description: string
  estimatedWeight: string
  urgency?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  specialInstructions?: string
  contactName?: string
  contactPhone?: string
  pickupStreet?: string
  pickupCity?: string
  pickupState?: string
  pickupZipCode?: string
  pickupCountry?: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledDate?: string
  pickupTime?: string
  imageUrl?: string
  items?: DonationItem[]
}

function combineDateAndTime(dateStr?: string, timeStr?: string): string | undefined {
  if (!dateStr || !timeStr) return undefined
  // If timeStr is already a full datetime (e.g., 2026-01-01T10:00), keep as-is
  if (timeStr.includes('T')) return timeStr
  // If dateStr is full datetime (from legacy datetime-local), keep date part
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr
  return `${datePart}T${timeStr}`
}

// Get all donations with optional filters
export async function getDonations(filters?: {
  status?: string
  urgency?: string
}): Promise<Donation[]> {
  try {
    const queryParams = new URLSearchParams()
    if (filters?.status) queryParams.append('status', filters.status)
    if (filters?.urgency) queryParams.append('urgency', filters.urgency)

    const url = `${API_BASE_URL}/api/donations${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    const response = await fetch(url)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch donations')
    }

    return (data.data || []).filter((donation: Donation) => !isHiddenDirectoryOrg(donation))
  } catch (error) {
    console.error('Error fetching donations:', error)
    throw error
  }
}

// Get a specific donation by ID
export async function getDonation(id: string): Promise<Donation | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/${id}`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch donation')
    }

    if (!data.data || isHiddenDirectoryOrg(data.data)) {
      return null
    }

    return data.data
  } catch (error) {
    console.error('Error fetching donation:', error)
    return null
  }
}

// Create a new donation
export async function createDonation(donationData: CreateDonationData): Promise<Donation | null> {
  let retryCount = 0;
  const maxRetries = 2;
  
  while (retryCount <= maxRetries) {
    try {
      // Get fresh auth headers (force refresh on retry attempts)
      const forceRefresh = retryCount > 0;
      console.log(`📤 Creating donation (attempt ${retryCount + 1}/${maxRetries + 1})${forceRefresh ? ' with token refresh' : ''}...`)
      
      const authHeaders = await getAuthHeadersAsync(forceRefresh)
      
      if (!authHeaders.Authorization) {
        throw new Error('No authentication token available. Please log in again.')
      }

      // Normalize payload (forms may provide time-only strings, or legacy fields)
      const normalizedScheduledDate = donationData.scheduledDate || (donationData as any).availableUntil
      const normalizedPickupTime = combineDateAndTime(
        normalizedScheduledDate,
        donationData.pickupTime || (donationData as any).scheduledTime
      )

      const payload: CreateDonationData = {
        title: donationData.title,
        description: donationData.description,
        estimatedWeight: donationData.estimatedWeight,
        urgency: donationData.urgency || 'MEDIUM',
        imageUrl: donationData.imageUrl || undefined,
        items: donationData.items,
        ...(normalizedScheduledDate ? { scheduledDate: normalizedScheduledDate } : {}),
        ...(normalizedPickupTime ? { pickupTime: normalizedPickupTime } : {})
      }
      
      const response = await fetch(`${API_BASE_URL}/api/donations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (!response.ok) {
        // If 401, try refreshing token and retry
        if (response.status === 401 && retryCount < maxRetries) {
          console.log(`🔄 401 error (attempt ${retryCount + 1}/${maxRetries + 1}), attempting token refresh and retry...`)
          retryCount++
          // Wait a bit before retrying to allow token refresh to complete
          await new Promise(resolve => setTimeout(resolve, 1000))
          continue
        }
        
        // If we've exhausted retries or it's not a 401, throw error
        console.error(`❌ Donation creation failed:`, data.error || 'Unknown error')
        throw new Error(data.error || 'Failed to create donation')
      }

      // Success!
      console.log('✅ Donation created successfully')
      return data.data
    } catch (error: any) {
      // If it's a network error or token error, and we haven't exhausted retries, retry
      if (retryCount < maxRetries && (
        error.message?.includes('No authentication token') || 
        error.message?.includes('Token is not valid') ||
        error.message?.includes('401')
      )) {
        console.log(`🔄 Retrying after error (attempt ${retryCount + 1}/${maxRetries + 1}):`, error.message)
        retryCount++
        await new Promise(resolve => setTimeout(resolve, 1000))
        continue
      }
      
      // Otherwise, throw the error
      console.error('❌ Error creating donation:', error)
      throw error
    }
  }
  
  throw new Error('Failed to create donation after retries')
}

/** Upload donation photo file; returns filename to store as imageUrl. */
export async function uploadDonationPhoto(file: File): Promise<string> {
  const form = new FormData()
  form.append('photo', file)

  const authHeaders = await getAuthHeadersAsync(true)
  if (!authHeaders.Authorization) {
    throw new Error('No authentication token available. Please log in again.')
  }

  const response = await fetch(`${API_BASE_URL}/api/donations/photo`, {
    method: 'POST',
    headers: {
      Authorization: authHeaders.Authorization
    },
    body: form
  })

  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error(data.error || 'Failed to upload donation photo')
  }

  return data.data?.imageUrl as string
}

// Accept a donation (volunteers)
export async function acceptDonation(id: string, foodBankId?: string): Promise<Donation | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/${id}/accept`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(foodBankId ? { foodBankId } : {})
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to accept donation')
    }

    return data.data
  } catch (error) {
    console.error('Error accepting donation:', error)
    throw error
  }
}

// Mark donation as picked up (suppliers)
export async function markDonationPickedUp(id: string): Promise<Donation | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/${id}/pickup`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to mark donation as picked up')
    }

    return data.data
  } catch (error) {
    console.error('Error marking donation as picked up:', error)
    throw error
  }
}

// Mark donation as delivered (food banks)
export async function markDonationDelivered(id: string): Promise<Donation | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/${id}/deliver`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to mark donation as delivered')
    }

    return data.data
  } catch (error) {
    console.error('Error marking donation as delivered:', error)
    throw error
  }
}

// Cancel a donation
export async function cancelDonation(id: string): Promise<Donation | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/${id}/cancel`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to cancel donation')
    }

    return data.data
  } catch (error) {
    console.error('Error cancelling donation:', error)
    throw error
  }
}

// Get donations created by current user (suppliers)
export async function getMyCreatedDonations(opts?: { archived?: boolean }): Promise<Donation[]> {
  try {
    const query = opts?.archived ? '?archived=true' : ''
    const response = await fetch(`${API_BASE_URL}/api/donations/my/created${query}`, {
      headers: getAuthHeaders()
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch my created donations')
    }

    return data.data || []
  } catch (error) {
    console.error('Error fetching my created donations:', error)
    throw error
  }
}

export async function getMyArchivedDonations(): Promise<Donation[]> {
  return getMyCreatedDonations({ archived: true })
}

// Get donations accepted by current user (volunteers)
export async function getMyAcceptedDonations(): Promise<Donation[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/my/accepted`, {
      headers: getAuthHeaders()
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch my accepted donations')
    }

    return data.data || []
  } catch (error) {
    console.error('Error fetching my accepted donations:', error)
    throw error
  }
}

// Get incoming donations for current food bank (assigned deliveries)
export async function getMyIncomingDonations(): Promise<Donation[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/donations/my/incoming`, {
      headers: getAuthHeaders()
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch incoming deliveries')
    }

    return data.data || []
  } catch (error) {
    console.error('Error fetching incoming donations:', error)
    throw error
  }
}

// Legacy function for backward compatibility - maps to new donations API
export async function getMyPostedDeliveries(): Promise<Donation[]> {
  return getMyCreatedDonations()
}

export async function getMyClaimedDeliveries(): Promise<Donation[]> {
  return getMyAcceptedDonations()
}

export async function createDonationListing(donationData: CreateDonationData): Promise<Donation | null> {
  return createDonation(donationData)
}

// Legacy function for backward compatibility
export async function claimDelivery(id: string): Promise<Donation | null> {
  return acceptDonation(id)
}

// Legacy function for backward compatibility  
export async function getDeliveries(filters?: {
  status?: string
  urgency?: string
}): Promise<Donation[]> {
  return getDonations(filters)
}

// Legacy function for backward compatibility
export async function getDelivery(id: string): Promise<Donation | null> {
  return getDonation(id)
}

// Legacy function for backward compatibility - creates a donation request from food bank
export async function createPickupRequest(pickupData: {
  title: string
  description: string
  estimatedWeight: string
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  specialInstructions?: string
  contactName: string
  contactPhone: string
  pickupStreet: string
  pickupCity: string
  pickupState: string
  pickupZipCode: string
  pickupCountry?: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledDate?: string
  pickupTime?: string
  items?: DonationItem[]
}): Promise<Donation | null> {
  return createDonation(pickupData)
}
